import { InvoiceProps } from "../../../interfaces/invoices_types"
import { InvoiceFormBase } from "./InvoiceFormBase"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { updateInvoice } from "@/api/invoices"
import { Alert } from "@/components/_Shared/Form/Buttons/Alert"
import { useEffect } from "react"
import { useFormData } from "@/components/_Shared/Form/Hooks/useFormDataFetch"
import { UserSchema } from "@/components/_Shared/Form/Schemas/ManagerSchema"
import { Error_Component } from "@/components/misc"

const invoiceSchema = z.object({
    invoice_id: z.string(),
    invoice_date: z.string().min(1, {message: "Invoice date is required"}),
    payment_status: z.string().min(1, {message: "Payment status is required"}),
    payment_amount: z.number().min(0, {message: "Payment amount must be greater than 0"}),
    project_name: z.string().min(1, {message: "Project name is required"}),
    project_id: z.string(),
    notes: z.string().optional(),
    is_sent_to_quickbooks: z.boolean().optional(),
})
.merge(UserSchema)

export type InvoiceFormData = z.infer<typeof invoiceSchema>

/**
 * This is mainly for updating invoices.
 * For creating invoices, use the Project Form.
 */
export function InvoiceForm({ method, current_invoice_data }: { method: string, current_invoice_data?: InvoiceProps }) {
    // const navigate = useNavigate()

    const currentInvoiceData: InvoiceProps = current_invoice_data 
        ? { ...current_invoice_data, project_id: current_invoice_data.project, payment_status: "Sent" }
        : {
                invoice_id: "",
                project_id: "",
                invoice_date: "",
                payment_status: "Sent", 
                payment_amount: 0,
                project_name: "",
                user_sent_by_name: "",
                is_sent_to_quickbooks: false
            }

    const { projectManagers, fetchAllDataProject } = useFormData()

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting, isSubmitSuccessful },
        control,
        setError,
    } = useForm<InvoiceFormData>({
        defaultValues: currentInvoiceData ? ImportFormProps(currentInvoiceData) : undefined,
        resolver: zodResolver(invoiceSchema),
    })

    const onSubmit = async (data: InvoiceFormData) => {
        const formatted_data = {
            ...data,
            user_sent_by: data.user_sent_by_name?.value
        }
        
        const response = await updateInvoice(formatted_data as InvoiceProps)

        if (response.operation != "COMPLETED") {
            setError("root", {message: "Invoice update failed"})
        }
    }

    useEffect(() => {
        fetchAllDataProject()
    }, [fetchAllDataProject])

    return (
        <>
            {errors.root && <Error_Component errorString={errors.root.message ?? "An error occurred"} />}

            <InvoiceFormBase 
                method={method} 
                currentInvoiceData={currentInvoiceData}
                onSubmit={handleSubmit(onSubmit)} 
                register={register}
                errors={errors}
                isSubmitting={isSubmitting}
                control={control}
                manager_list={projectManagers}
            />

            {isSubmitSuccessful && <Alert 
                title="Invoice updated successfully" 
                description="The invoice has been updated successfully." 
                redirects={[
                    {label: "To Invoices", value: "/invoices"},
                    {label: "To Dashboard", value: "/main_menu"}
                ]}
            />}
        </>
    )
}

function ImportFormProps(currentInvoiceData: InvoiceProps) {
    return {
        invoice_id: currentInvoiceData.invoice_id,
        project_id: currentInvoiceData.project,
        invoice_date: currentInvoiceData.invoice_date, 
        payment_status: currentInvoiceData.payment_status,
        payment_amount: currentInvoiceData.payment_amount,
        project_name: currentInvoiceData.project_name,
        user_sent_by_name: currentInvoiceData.user_sent_by_name ? {
            value: currentInvoiceData.user_email,
            label: currentInvoiceData.user_sent_by_name
        } : null,
        notes: currentInvoiceData.notes,
        is_sent_to_quickbooks: currentInvoiceData.is_sent_to_quickbooks
    }
}